import { ComboBox as RACComboBox, Input, Button } from "react-aria-components";
import { Key } from "@react-types/shared";
import { X } from "react-feather";
import clsx from "clsx";

import DropdownPopover from "../dropdown-popover";

import ListBox from "./list-box";
import styles from "./combo-box.module.css";

/**
 * A typeahead over a set too large to browse by scrolling.
 *
 * Use `Select` when someone is picking from a closed set they can scan by
 * opening the trigger. Use `ComboBox` when the set is large enough that
 * typing is how a person actually finds the one they want - a roster, a
 * catalog - and picking still has to work the way a native `<select>` does:
 * arrow keys move a highlight, and Enter commits whatever is highlighted.
 * `Select`'s `search` prop puts a `SearchField` inside the popover instead,
 * and that field's Enter key does not select anything - it only keeps
 * typing. `ComboBox` is built on react-aria-components' own `ComboBox`
 * specifically to get that keyboard behaviour, and the correct
 * `aria-autocomplete` semantics that come with it, for free.
 *
 * Filtering `items` down to what matches is the consumer's job, exactly as it
 * is for `Select`'s `search` prop - this renders `items` as given, so no
 * BFC-specific matching rule (case folding, matching more than one field)
 * lives in pallet.
 */
interface ComboBoxProps {
  items: Array<{ id: string; label: string }>;
  inputValue: string;
  onInputChange: (value: string) => void;
  selectedId?: string | null;
  onSelectionChange: (id: string | null) => void;
  /**
   * Drops the current selection, and draws the button that does it.
   *
   * Separate from `onSelectionChange(null)` because they are different
   * gestures with different consequences for the consumer: a selection change
   * is a pick, and clearing is a pick being taken back - which for a filter
   * usually also means resetting whatever typing state was tracking it. The
   * button only appears while `selectedId` is set, so a field nobody has
   * picked in does not offer to undo a pick.
   */
  onClear?: () => void;
  /** Names the clear button for a screen reader; there is no visible text. */
  clearLabel?: string;
  /** Lets the consumer fetch lazily on first open rather than on mount. */
  onOpenChange?: (isOpen: boolean) => void;
  isLoading?: boolean;
  placeholder?: string;
  "aria-label"?: string;
  /** Drawn in the popover when `items` is empty. */
  emptyLabel?: string;
  /** Drawn in the popover instead of `items` while `isLoading` is true. */
  loadingLabel?: string;
  className?: string;
  "data-testid"?: string;
}

function ComboBox({
  items,
  inputValue,
  onInputChange,
  selectedId,
  onSelectionChange,
  onClear,
  clearLabel = "Clear selection",
  onOpenChange,
  isLoading = false,
  placeholder,
  "aria-label": ariaLabel,
  emptyLabel,
  loadingLabel,
  className,
  "data-testid": testId,
}: ComboBoxProps) {
  return (
    <RACComboBox
      aria-label={ariaLabel}
      data-testid={testId}
      inputValue={inputValue}
      onInputChange={onInputChange}
      selectedKey={selectedId ?? null}
      onSelectionChange={(key: Key | null) =>
        onSelectionChange(key === null ? null : String(key))
      }
      // Narrowed rather than passed straight through: react-aria-components
      // calls this with a second argument naming what opened the field
      // ("focus", "manual"), which is not part of this component's contract -
      // and a prop typed to one argument that hands over two is how a consumer
      // ends up depending on something we never promised.
      onOpenChange={(isOpen) => onOpenChange?.(isOpen)}
      // The consumer's roster can be empty (nothing matches, or nothing has
      // loaded yet) and that has to stay a valid state to open into, not an
      // error react-aria-components throws.
      allowsEmptyCollection
      // Opening on focus, not only on the caret click, is what makes
      // clicking into the field the same "start fetching now" gesture the
      // consumer's `onOpenChange` is for.
      menuTrigger="focus"
      className={clsx(styles.comboBox, className)}
    >
      <div className={styles.fieldWrap}>
        <Input
          className={styles.input}
          placeholder={placeholder}
          data-testid="combo-box-input"
        />
        {onClear && selectedId !== null && selectedId !== undefined ? (
          /*
            `slot={null}` is what keeps this from becoming a second trigger.
            react-aria-components' ComboBox publishes the trigger's props on
            ButtonContext, so an unslotted Button anywhere under it inherits
            them - opting out is how a button under a ComboBox does anything
            other than open the popover.

            Deliberately before the trigger in source order, so it is also
            before it for the keyboard: the reading order down the field is
            what you typed, the way to unpick it, and the way to see the rest.
          */
          <Button
            slot={null}
            className={styles.clear}
            aria-label={clearLabel}
            onPress={onClear}
            // Reachable by pointer and by screen reader, but skipped when
            // tabbing: it is an undo for a value the field already shows, and
            // a tab stop between the input and the trigger would sit in the
            // way of every keyboard user who is only trying to open the list.
            excludeFromTabOrder
            data-testid="combo-box-clear"
          >
            <X size={14} aria-hidden="true" />
          </Button>
        ) : null}
        <Button className={styles.trigger} data-testid="combo-box-trigger">
          <span aria-hidden="true">▾</span>
        </Button>
      </div>
      <DropdownPopover className={styles.popover}>
        {isLoading && loadingLabel ? (
          <div className={styles.status} data-testid="combo-box-loading">
            {loadingLabel}
          </div>
        ) : items.length === 0 && emptyLabel ? (
          <div className={styles.status} data-testid="combo-box-empty">
            {emptyLabel}
          </div>
        ) : (
          <ListBox items={items} />
        )}
      </DropdownPopover>
    </RACComboBox>
  );
}

export default ComboBox;
